/**
 * iter307e — `<SortableTable>`: a single primitive for every admin
 * table that needs sortable headers + pagination-safe sort.
 *
 * iter307ar — extended with:
 *   • Row selection (`selectable`, `selectedKeys`, `onSelectionChange`,
 *     `isRowSelectable`) — header checkbox toggles current-page selection
 *     of eligible rows.
 *   • External sort control (`sortState`, `onSortChange`) for tables
 *     that sort server-side; internal state is used only when these
 *     props are omitted.
 *   • External pagination (`currentPage`, `totalCount`, `onPageChange`)
 *     for server-paginated tables; client-side pagination is used only
 *     when these props are omitted.
 *
 * Why this exists
 * ---------------
 * Each table in this codebase historically reimplemented its own
 * sort comparator → 5+ subtly different bugs across Enrollments,
 * Invoice Management, Parent Invoices, Transactions. Symptoms
 * included: dates string-compared, null values floating to the wrong
 * end, 3rd-click on a header doing nothing, sort happening AFTER
 * pagination so each page sorted independently, etc.
 *
 * `<SortableTable>` centralises all of that:
 *   - One comparator (`sortRows`) with type-aware handling
 *     (date / numeric / string) and consistent null-sinks-bottom.
 *   - Sort happens on the FULL dataset before pagination slicing
 *     (client mode). Server-mode delegates to the caller.
 *   - Cycle-sort click reducer (`nextSortState`) gives a 3-state
 *     header: asc → desc → clear.
 *   - Visual indicator on the active column (↑/↓), neutral icon
 *     elsewhere (↕).
 *   - `data-testid` + `data-sort-active` on every header for QA.
 *
 * Minimal client-side usage
 * -------------------------
 * ```jsx
 * <SortableTable
 *   data={rows}
 *   columns={COLUMNS}
 *   defaultSort={{ field: 'date', direction: 'desc' }}
 *   pageSize={25}
 *   getRowKey={(r) => r.id}
 *   testidPrefix="invoices"
 * />
 * ```
 *
 * Server-paginated + bulk-select usage
 * ------------------------------------
 * ```jsx
 * <SortableTable
 *   data={pageRows}                       // current page only
 *   columns={COLUMNS}
 *   sortState={{ field, direction }}      // controlled
 *   onSortChange={(next) => setSort(next)}
 *   currentPage={page}
 *   totalCount={totalRows}
 *   pageSize={25}
 *   onPageChange={(p) => setPage(p)}
 *   selectable
 *   selectedKeys={selectedIds}
 *   onSelectionChange={(ids) => setSelectedIds(ids)}
 *   isRowSelectable={(r) => r.status !== 'paid'}
 *   getRowKey={(r) => r.id}
 * />
 * ```
 */
import React, { useMemo, useState } from 'react';
import { ArrowUpDown, ArrowUp, ArrowDown, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { Checkbox } from '../checkbox';
import { sortRows, nextSortState } from './sortRows';

const _renderCell = (col, row) => {
  if (col.render) return col.render(row);
  const accessor = col.accessor || ((r) => r[col.key]);
  const v = accessor(row);
  return v == null || v === '' ? <span className="text-slate-400">—</span> : String(v);
};

export default function SortableTable({
  data = [],
  columns = [],
  defaultSort = null,
  pageSize = 25,
  getRowKey = (r, i) => r?.id ?? i,
  onRowClick,
  testidPrefix = 'table',
  emptyMessage = 'No rows to display',
  loading = false,
  className = '',
  // ---- iter307ar — external sort control (server-sorted tables) ----
  sortState = null,           // { field, direction } — when set, sort is external
  onSortChange = null,        // (next) => void
  // ---- iter307ar — external pagination (server-paginated tables) ----
  currentPage: controlledPage = null,
  totalCount = null,          // when set with onPageChange, pagination is external
  onPageChange = null,
  // ---- iter307ar — row selection ----
  selectable = false,
  selectedKeys = null,        // array of row keys (controlled)
  onSelectionChange = null,   // (nextKeys) => void
  isRowSelectable = null,     // (row) => boolean — defaults to all rows
}) {
  const isServerSort = sortState !== null && onSortChange !== null;
  const isServerPagination = controlledPage !== null && totalCount !== null && onPageChange !== null;

  // Internal sort state (client-side mode only)
  const [internalSort, setInternalSort] = useState(defaultSort || { field: null, direction: 'asc' });
  const sort = isServerSort ? sortState : internalSort;

  // Internal page (client-side mode only)
  const [internalPage, setInternalPage] = useState(1);
  const page = isServerPagination ? controlledPage : internalPage;

  // Reset internal page when sort or data length changes so the user never
  // lands on a stale slice after a sort click. (No-op in server mode.)
  const dataLength = data.length;
  React.useEffect(() => {
    if (!isServerPagination) setInternalPage(1);
  }, [sort.field, sort.direction, dataLength, isServerPagination]);

  // Client-side sort (only when not externally controlled)
  const sortedData = useMemo(
    () => (isServerSort ? data : sortRows(data, sort, columns)),
    [data, sort, columns, isServerSort],
  );

  // Pagination
  const totalRows = isServerPagination ? totalCount : sortedData.length;
  const totalPages = pageSize > 0 ? Math.max(1, Math.ceil(totalRows / pageSize)) : 1;
  const currentPage = Math.min(page, totalPages);
  const slice = isServerPagination
    ? sortedData                  // server returns just the current page
    : pageSize > 0
      ? sortedData.slice((currentPage - 1) * pageSize, currentPage * pageSize)
      : sortedData;

  const goToPage = (p) => {
    if (isServerPagination) onPageChange(p);
    else setInternalPage(p);
  };

  const handleHeaderClick = (col) => {
    if (col.sortable === false) return;
    const next = nextSortState(sort, col.key);
    if (isServerSort) onSortChange(next);
    else setInternalSort(next);
  };

  // ---- Selection helpers ----
  const selectableRowsInSlice = useMemo(
    () => (selectable
      ? slice.filter((r) => (isRowSelectable ? isRowSelectable(r) : true))
      : []),
    [slice, selectable, isRowSelectable],
  );
  const sliceKeys = useMemo(
    () => selectableRowsInSlice.map((r, i) => String(getRowKey(r, i))),
    [selectableRowsInSlice, getRowKey],
  );
  const selectedSet = useMemo(() => new Set((selectedKeys || []).map(String)), [selectedKeys]);
  const allPageSelected = sliceKeys.length > 0 && sliceKeys.every((k) => selectedSet.has(k));
  const somePageSelected = sliceKeys.some((k) => selectedSet.has(k));

  const togglePageSelection = () => {
    if (!onSelectionChange) return;
    const next = new Set(selectedKeys || []);
    if (allPageSelected) {
      sliceKeys.forEach((k) => next.delete(k));
    } else {
      sliceKeys.forEach((k) => next.add(k));
    }
    onSelectionChange(Array.from(next));
  };

  const toggleRowSelection = (rowKey) => {
    if (!onSelectionChange) return;
    const k = String(rowKey);
    const next = new Set(selectedKeys || []);
    if (next.has(k)) next.delete(k);
    else next.add(k);
    onSelectionChange(Array.from(next));
  };

  return (
    <div className={`relative ${className}`} data-testid={`${testidPrefix}-table`}>
      {loading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 backdrop-blur-sm">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              {selectable && (
                <th className="px-3 py-2 w-10" data-testid={`${testidPrefix}-select-all-cell`}>
                  <Checkbox
                    checked={allPageSelected}
                    onCheckedChange={togglePageSelection}
                    aria-label="Select all rows on this page"
                    data-testid={`${testidPrefix}-select-all`}
                    className={somePageSelected && !allPageSelected ? 'data-[state=unchecked]:bg-slate-200' : ''}
                    disabled={sliceKeys.length === 0}
                  />
                </th>
              )}
              {columns.map((col) => {
                const isActive = sort.field === col.key;
                const sortable = col.sortable !== false;
                const Icon = isActive ? (sort.direction === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
                return (
                  <th
                    key={col.key}
                    className={`px-3 py-2 select-none ${
                      sortable ? 'cursor-pointer hover:bg-slate-100' : ''
                    } ${isActive ? 'bg-slate-100 text-slate-700' : ''} ${col.className || ''}`}
                    style={col.width ? { width: col.width } : undefined}
                    onClick={() => handleHeaderClick(col)}
                    data-testid={`${testidPrefix}-sort-${col.key}`}
                    data-sort-active={isActive ? sort.direction : 'none'}
                  >
                    <div
                      className={`flex items-center gap-1 ${
                        col.align === 'right' ? 'justify-end' :
                        col.align === 'center' ? 'justify-center' : ''
                      }`}
                    >
                      {col.label}
                      {sortable && (
                        <Icon className={`h-3 w-3 ${isActive ? 'text-slate-700' : 'text-slate-400'}`} />
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {slice.length === 0 && !loading ? (
              <tr>
                <td colSpan={columns.length + (selectable ? 1 : 0)} className="px-3 py-12 text-center text-sm text-slate-500" data-testid={`${testidPrefix}-empty`}>
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              slice.map((row, i) => {
                const k = getRowKey(row, i);
                const rowKeyStr = String(k);
                const rowSelectable = !selectable || (isRowSelectable ? isRowSelectable(row) : true);
                const rowSelected = selectable && selectedSet.has(rowKeyStr);
                return (
                  <tr
                    key={k}
                    className={`${onRowClick ? 'cursor-pointer ' : ''}hover:bg-slate-50 ${rowSelected ? 'bg-blue-50/50' : ''}`}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    data-testid={`${testidPrefix}-row-${k}`}
                    data-selected={rowSelected ? 'true' : 'false'}
                  >
                    {selectable && (
                      <td
                        className="px-3 py-2 w-10"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Checkbox
                          checked={rowSelected}
                          onCheckedChange={() => toggleRowSelection(k)}
                          disabled={!rowSelectable}
                          aria-label="Select row"
                          data-testid={`${testidPrefix}-select-row-${k}`}
                        />
                      </td>
                    )}
                    {columns.map((col) => (
                      <td
                        key={col.key}
                        className={`px-3 py-2 ${
                          col.align === 'right' ? 'text-right' :
                          col.align === 'center' ? 'text-center' : ''
                        } ${col.className || ''}`}
                      >
                        {_renderCell(col, row)}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {pageSize > 0 && totalRows > pageSize && (
        <div className="flex items-center justify-between border-t border-slate-100 px-3 py-2 text-xs text-slate-500" data-testid={`${testidPrefix}-pagination`}>
          <span>
            Showing {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, totalRows)} of {totalRows}
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              className="px-2 py-1 rounded hover:bg-slate-100 disabled:opacity-40"
              disabled={currentPage <= 1}
              onClick={() => goToPage(currentPage - 1)}
              data-testid={`${testidPrefix}-page-prev`}
            >
              <ChevronLeft className="h-3 w-3" />
            </button>
            <span className="px-2">Page {currentPage} of {totalPages}</span>
            <button
              type="button"
              className="px-2 py-1 rounded hover:bg-slate-100 disabled:opacity-40"
              disabled={currentPage >= totalPages}
              onClick={() => goToPage(currentPage + 1)}
              data-testid={`${testidPrefix}-page-next`}
            >
              <ChevronRight className="h-3 w-3" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
